import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  PageHeader,
  errorMessage,
  useAuth,
  useUmClient,
  type AdminTestEmailResponse as TestEmailResult,
} from "@nexys/user-management-web";
import { AlertCircle, MailCheck, Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";

/**
 * Superadmin utility: send a test email through whatever provider the
 * server was deployed with (console, Resend, Mandrill, Cloudflare).
 * The success panel echoes provider + from so an operator can confirm
 * the deployment config at a glance; a provider failure surfaces the
 * provider's own error text (that's the point of the page).
 */
export const EmailTestPage = () => {
  const { data, hasUserRole } = useAuth();
  const client = useUmClient();
  const [to, setTo] = useState(data?.user.email ?? "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState<TestEmailResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!hasUserRole("superadmin")) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSent(null);
    const res = await client.adminSendTestEmail({ to: to.trim() });
    setLoading(false);
    if (res.ok) {
      setSent(res.data);
    } else {
      setError(errorMessage(res.error, "Couldn't send the test email."));
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Email test"
        description="Send a test email through the server's configured mail provider to verify credentials and the sender address."
      />
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="size-4 text-muted-foreground" />
            Send a test email
          </CardTitle>
          <CardDescription>
            Uses the deployment&apos;s <code>MAIL_PROVIDER</code> — the same path
            password resets, invitations and magic links go through.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4">
            <Field label="Recipient" htmlFor="email-test-to" required>
              <Input
                id="email-test-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            {sent ? (
              <Alert>
                <MailCheck className="size-4" />
                <AlertDescription>
                  Sent to <strong>{sent.to}</strong> via{" "}
                  <strong>{sent.provider}</strong>
                  {sent.from ? (
                    <>
                      {" "}
                      from <strong>{sent.from}</strong>
                    </>
                  ) : null}
                  . Check the inbox (and spam folder).
                </AlertDescription>
              </Alert>
            ) : null}
            <div>
              <Button type="submit" disabled={loading || to.trim().length === 0}>
                {loading ? "Sending…" : "Send test email"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
